import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import AdminPortalLayout from './AdminPortalLayout';
import { AdminMe, fetchSubscriptions, SubscriptionRow } from '../lib/admin-portal-api';
import { FaStar, FaExclamationTriangle } from '../lib/icons';
import { PlatformBadge, StatusPill } from './SubscriptionBadges';

interface Props { me: AdminMe }

const STATUSES = ['', 'active', 'past_due', 'canceled', 'expired'] as const;
const PRETTY: Record<string, string> = {
  '': 'All', active: 'Active', past_due: 'Past due', canceled: 'Canceled', expired: 'Expired',
};

export default function AdminPortalSubscriptions({ me }: Props) {
  const [status, setStatus] = useState<string>('active');
  const [rows, setRows] = useState<SubscriptionRow[]>([]);
  const [counts, setCounts] = useState<any>(null);
  const [pagination, setPagination] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async (pageNum = 1, append = false) => {
    if (!append) setLoading(true);
    try {
      const res = await fetchSubscriptions(status, pageNum, 20);
      setRows((prev) => append ? [...prev, ...res.data] : res.data);
      setCounts(res.counts);
      setPagination(res.pagination);
      setPage(pageNum);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(1); }, [status]);

  return (
    <AdminPortalLayout me={me} active="subscriptions">
      <Helmet><title>Subscriptions — EatMeat Admin</title></Helmet>

      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Subscriptions</h1>
          <p className="text-sm text-gray-500 mt-1">All paid + lapsed subscriptions across iOS, Android, web.</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-1 flex gap-1 overflow-x-auto">
          {STATUSES.map((s) => (
            <button key={s || 'all'} onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                status === s ? 'bg-primary text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'
              }`}>
              {PRETTY[s]}
              {counts && s && counts[s] !== undefined && (
                <span className="ml-1 text-[10px] opacity-80">{counts[s]}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 inline-flex items-start gap-2 mb-4">
          <FaExclamationTriangle className="mt-0.5 text-red-500 shrink-0" /> {error}
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-14 bg-white rounded-xl animate-pulse" />)}</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
          <FaStar className="text-4xl mx-auto mb-3 text-gray-200" />
          <p className="text-sm text-gray-500">No subscriptions match.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Platform</th>
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Period</th>
                <th className="px-4 py-3 text-right">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    {s.user ? (
                      <Link to={`/admin-portal/users/${s.user.id}`} className="block">
                        <div className="font-semibold text-gray-800 truncate max-w-xs">{s.user.username || '—'}</div>
                        <div className="text-xs text-gray-500 truncate max-w-xs">{s.user.email}</div>
                      </Link>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={s.subscription_status} />
                    {s.cancel_at_period_end && (
                      <div className="text-[10px] text-amber-600 mt-0.5">cancels at period end</div>
                    )}
                  </td>
                  <td className="px-4 py-3"><PlatformBadge value={s.platform_origin} /></td>
                  <td className="px-4 py-3 text-xs text-gray-500 break-words max-w-xs">{s.apple_product_id || '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-700">
                    {s.current_period_start ? <>{new Date(s.current_period_start).toLocaleDateString()}</> : '—'}
                    {s.current_period_end ? <> → {new Date(s.current_period_end).toLocaleDateString()}</> : null}
                  </td>
                  <td className="px-4 py-3 text-right text-[11px] text-gray-400">
                    {new Date(s.updatedAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {pagination && (
            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
              <span>Showing {rows.length} of {pagination.total}</span>
              {page < pagination.pageCount && (
                <button onClick={() => load(page + 1, true)}
                  className="px-3 py-1.5 rounded-md bg-gray-50 hover:bg-gray-100 font-semibold text-gray-700">
                  Load more
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </AdminPortalLayout>
  );
}

